// @ts-strict

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { dropdownValues } from '@app/core/helpers/constants';
import { checkDateFormat } from '@app/core/helpers/tools';
import { DefaultApiType } from '@app/core/models/core-models';
import { environment } from '@env/environment';
import {
  FilterMenuOptions,
  GraphFilterOption,
  StatisticsColumnManagmentNumberType,
  StatsRequestParams,
  StatsRequestParamsMap
} from '@pages/statistics/shared/interfaces/statistics';
import { StatisticsMapsGraphService } from '@pages/statistics/shared/statistics-graphs/statistics-maps-graph/statistics-maps-graph.service';
import { StatisticsService } from '@pages/statistics/statistics.service';
import { StatisticsGraphsService } from '@services/statistics/statistics-graphs.service';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { AvailableStatisticsTableColumns } from '../../shared/statistics-table/table.service';
import { VisitsGraphsStatisticsResponse, VisitsMapStatisticsResponse, VisitsStatisticsResponse } from './interface/visits-statistics';

@Injectable({
  providedIn: 'root'
})
export class VisitsStatisticsService {
  public defaultVisibleColumns: AvailableStatisticsTableColumns[] = ['configurationName', 'countries', 'refferingAddress', 'created'];

  public initialRequestParams: StatsRequestParams = {
    ...this.statisticsService.getInitialRequestDate(),
    order_field: 'id',
    order_type: 'desc',
    per_page: 50
  };

  public columnManagementNumbers: StatisticsColumnManagmentNumberType = {
    leads: [],
    earnings: [],
    others: [26, 32, 14, 15, 18, 20, 21, 22, 23, 25]
  };

  public dropdownValues = [
    dropdownValues[59],
    dropdownValues[60],
    dropdownValues[12],
    dropdownValues[13],
    dropdownValues[0],
    dropdownValues[1],
    dropdownValues[14],
    dropdownValues[15]
  ];

  public requestParamsMap: StatsRequestParamsMap = {
    campaignMenu: 'programs[]' || 'admin_programs[]',
    configurationNameMenu: 'configurations[]',
    campaign_type: 'types[]',
    countries: 'countries[]',
    admin_program_id: 'admin_programs[]',
    countriesMenu: 'countries[]',
    refferingMenu: 'referrer'
  };

  public graphFilterOptions: GraphFilterOption[] = [
    {
      id: 3,
      name: 'visits',
      translation: 'visits',
      status: true
    }
  ];

  public filterMenuOptions: FilterMenuOptions = {
    campaignMenu: [
      {
        category: 'sort',
        values: [dropdownValues[12], dropdownValues[13]]
      },
      {
        category: 'searchInput',
        name: 'campaign_id',
        searchMethod: 'id',
        values: null
      },
      // {
      //   category: 'selectWithSearch',
      //   name: 'admin_program_id',
      //   type: 'incent',
      // searchMethod: 'id',
      //   values: []
      // },
      {
        category: 'selectWithSearch',
        name: 'campaign_name_or_id',
        type: 'incent',
        values: []
      },
      {
        category: 'checkboxesList',
        values: {
          campaign_type: [
            {
              id: 1,
              text: 'CPL',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            },
            {
              id: 2,
              text: 'CPS',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            },
            {
              id: 3,
              text: 'CPA',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            },
            {
              id: 4,
              text: 'PPI',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            },
            {
              id: 5,
              text: 'COD',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            }
          ]
        }
      }
    ],
    visitIDMenu: [
      {
        category: 'sort',
        values: [dropdownValues[59], dropdownValues[60]]
      }
    ],
    configurationNameMenu: [
      {
        category: 'sort',
        values: [dropdownValues[14], dropdownValues[15]]
      },
      {
        category: 'searchInput',
        name: 'config_id',
        searchMethod: 'id',
        values: null
      }
    ],
    countriesMenu: [
      {
        category: 'countriesSelect',
        name: 'countries',
        values: []
      }
    ],
    refferingMenu: [
      {
        category: 'searchInput',
        name: 'referring_address',
        searchMethod: 'name',
        values: null
      }
    ],
    // mlSubMenu: Array.from({ length: 5 }, (_e, i) => ({
    //   category: 'searchInput',
    //   name: `ml_sub${i + 1}`,
    //   searchMethod: 'name',
    //   values: null
    // })),
    createdAtMenu: [{ category: 'sort', values: [dropdownValues[0], dropdownValues[1]] }]
  };

  constructor(
    private readonly http: HttpClient,
    private readonly statisticsGraphsService: StatisticsGraphsService,
    private readonly statisticsMapsGraphService: StatisticsMapsGraphService,
    private readonly statisticsService: StatisticsService
  ) {}

  public getVisitsStatisticsData(requestParams: StatsRequestParams): Observable<{ error: boolean } & Required<VisitsStatisticsResponse>> {
    const params = {
      ...requestParams,
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };

    return this.http.get<DefaultApiType<VisitsStatisticsResponse>>(environment.apiUrl + `/statistics/visits`, { params }).pipe(
      map(({ data, status_code }) => {
        if (status_code && status_code === 200 && data && data.pagination && data.summary && data.visits) {
          const { pagination, summary, visits } = data;

          return { error: false, pagination, summary, visits };
        }

        if (status_code && status_code === 200 && data && data.pagination && data.visits && params.summary === 0) {
          const { pagination, visits } = data;

          return { error: false, pagination, summary: { visits: 0 }, visits };
        }

        return { error: true, visits: [], summary: { visits: 0 }, pagination: {} };
      })
    );
  }

  public getVisitsMapStatisticsData = (requestParams: StatsRequestParams): Observable<{ error: boolean } & Required<VisitsMapStatisticsResponse>> => {
    this.statisticsMapsGraphService.setIsLoading(true);

    const { order_field, order_type, page, ...rest } = requestParams;

    const params = {
      ...rest,
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };

    return this.http.get<DefaultApiType<VisitsMapStatisticsResponse>>(environment.apiUrl + `/statistics/visits/map`, { params }).pipe(
      map(({ status_code, data }) => {
        if (status_code && status_code === 200 && data && data.map_statistics) {
          const { map_statistics } = data;

          return { error: false, map_statistics };
        }

        return { error: true, map_statistics: [] };
      }),
      finalize(() => this.statisticsMapsGraphService.setIsLoading(false))
    );
  };

  public getVisitsGraphsStatisticsData = (requestParams: StatsRequestParams): Observable<{ error: boolean } & Required<VisitsGraphsStatisticsResponse>> => {
    this.statisticsGraphsService._loadingGraphDataSubject.next(true);

    const { order_field, order_type, page, ...rest } = requestParams;

    const params = {
      ...rest,
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };

    return this.http.get<DefaultApiType<VisitsGraphsStatisticsResponse>>(environment.apiUrl + `/statistics/visits/chart`, { params }).pipe(
      map(({ data, status_code }) => {
        if (status_code && status_code === 200 && data && data.chart_statistics) {
          const { chart_statistics } = data;

          return { error: false, chart_statistics };
        }

        return { error: true, chart_statistics: [] };
      }),
      finalize(() => this.statisticsGraphsService._loadingGraphDataSubject.next(false))
    );
  };
}
